import { Icon } from "@chakra-ui/react";
import { useMemo } from "react";
import { MdErrorOutline } from "react-icons/md";
import TableFilterSelect from "components/Select/TableFilterSelect";
import FilterButton from "components/Button/FilterButton";
import FilterSortSearchBar from "components/ControlBar/FilterSortSearchBar";
import { ActiveFilter, SortOption } from "components/ControlBar/types";
import { RELATION_TARET_TYPE } from "definitions/constants";

const stakeholderOptions = [
  { label: "New Hire", value: RELATION_TARET_TYPE.NEW_HIRE },
  { label: "Manager", value: RELATION_TARET_TYPE.MANAGER },
  { label: "People Team", value: RELATION_TARET_TYPE.PEOPLE_TEAM },
  { label: "Buddy", value: RELATION_TARET_TYPE.BUDDY },
];

const actionTypeOptions = [
  { label: "Email", value: "email" },
  { label: "Meeting", value: "meeting" },
  { label: "Message", value: "message" },
  { label: "Task", value: "task" },
];

interface ScheduleFiltersProps {
  searchText: string;
  onSearchChange: (value: string) => void;
  hasIssues: boolean;
  onHasIssuesChange: (value: boolean) => void;
  viewUnsent: boolean;
  onViewUnsentChange: (value: boolean) => void;
  viewCompleted: boolean;
  onViewCompletedChange: (value: boolean) => void;
  viewUpcoming?: boolean;
  onViewUpcomingChange?: (value: boolean) => void;
  selectedStakeholder: { label: string; value: string } | null;
  onStakeholderChange: (value: { label: string; value: string } | null) => void;
  selectedActionType: { label: string; value: string } | null;
  onActionTypeChange: (value: { label: string; value: string } | null) => void;
  onReset: () => void;
  currentSort: string;
  defaultSort: string;
  onSortChange: (value: string) => void;
  sortOptions: SortOption[];
}

export default function ScheduleFilters({
  searchText,
  onSearchChange,
  hasIssues,
  onHasIssuesChange,
  viewUnsent,
  onViewUnsentChange,
  viewCompleted,
  onViewCompletedChange,
  viewUpcoming,
  onViewUpcomingChange,
  selectedStakeholder,
  onStakeholderChange,
  selectedActionType,
  onActionTypeChange,
  onReset,
  currentSort,
  defaultSort,
  onSortChange,
  sortOptions,
}: ScheduleFiltersProps) {
  const relationTargetType = selectedStakeholder?.value || "";
  const actionType = selectedActionType?.value || "";

  const hasActiveFilters =
    selectedStakeholder !== null ||
    selectedActionType !== null ||
    hasIssues ||
    viewUnsent ||
    viewCompleted ||
    !!viewUpcoming;

  // Build active filters for display
  const activeFilters: ActiveFilter[] = useMemo(() => {
    const filters: ActiveFilter[] = [];

    if (relationTargetType) {
      filters.push({
        key: "stakeholder",
        label: `Stakeholder: ${selectedStakeholder?.label || "Unknown"}`,
        onRemove: () => onStakeholderChange(null),
      });
    }

    if (actionType) {
      filters.push({
        key: "type",
        label: `Type: ${selectedActionType?.label || "Unknown"}`,
        onRemove: () => onActionTypeChange(null),
      });
    }

    if (hasIssues) {
      filters.push({
        key: "has_issues",
        label: "With Issues",
        onRemove: () => onHasIssuesChange(false),
      });
    }

    if (viewUnsent) {
      filters.push({
        key: "view_unsent",
        label: "View Failed",
        onRemove: () => onViewUnsentChange(false),
      });
    }

    if (viewCompleted) {
      filters.push({
        key: "view_completed",
        label: "View Past",
        onRemove: () => onViewCompletedChange(false),
      });
    }

    if (viewUpcoming) {
      filters.push({
        key: "view_upcoming",
        label: "View Upcoming",
        onRemove: () => onViewUpcomingChange?.(false),
      });
    }

    return filters;
  }, [
    relationTargetType,
    actionType,
    hasIssues,
    viewUnsent,
    viewCompleted,
    viewUpcoming,
    selectedStakeholder,
    selectedActionType,
    onStakeholderChange,
    onActionTypeChange,
    onHasIssuesChange,
    onViewUnsentChange,
    onViewCompletedChange,
    onViewUpcomingChange,
  ]);

  return (
    <FilterSortSearchBar
      searchPlaceholder="Search actions..."
      searchValue={searchText}
      onSearchChange={onSearchChange}
      currentSort={currentSort}
      defaultSort={defaultSort}
      onSortChange={onSortChange}
      sortOptions={sortOptions}
      quickFilters={
        <>
          <FilterButton
            onClick={() => onHasIssuesChange(!hasIssues)}
            isActive={hasIssues}
            icon={<Icon as={MdErrorOutline} color="statusRed.text" boxSize={4} />}
            size="sm"
          >
            With Issues
          </FilterButton>
          <FilterButton
            onClick={() => {
              const newValue = !viewUnsent;
              onViewUnsentChange(newValue);
              if (newValue) {
                onViewCompletedChange(false);
                onViewUpcomingChange?.(false);
              }
            }}
            isActive={viewUnsent}
            size="sm"
          >
            View Failed
          </FilterButton>
        </>
      }
      allFilters={
        <>
          <FilterButton
            onClick={() => onHasIssuesChange(!hasIssues)}
            isActive={hasIssues}
            icon={<Icon as={MdErrorOutline} boxSize={4} />}
          >
            With Issues
          </FilterButton>
          <FilterButton
            onClick={() => {
              const newValue = !viewUnsent;
              onViewUnsentChange(newValue);
              if (newValue) {
                onViewCompletedChange(false);
                onViewUpcomingChange?.(false);
              }
            }}
            isActive={viewUnsent}
          >
            View Failed
          </FilterButton>
          <FilterButton
            onClick={() => {
              const newValue = !viewCompleted;
              onViewCompletedChange(newValue);
              if (newValue) {
                onViewUnsentChange(false);
                onViewUpcomingChange?.(false);
              }
            }}
            isActive={viewCompleted}
          >
            View Past
          </FilterButton>
          {onViewUpcomingChange && (
            <FilterButton
              onClick={() => {
                const newValue = !viewUpcoming;
                onViewUpcomingChange(newValue);
                if (newValue) {
                  onViewUnsentChange(false);
                  onViewCompletedChange(false);
                }
              }}
              isActive={viewUpcoming || false}
            >
              View Upcoming
            </FilterButton>
          )}
          <TableFilterSelect
            placeholder="Filter by Stakeholder"
            value={selectedStakeholder}
            onChange={onStakeholderChange}
            defaultOptions={stakeholderOptions}
            minWidth="180px"
            isClearable={false}
          />
          <TableFilterSelect
            placeholder="Filter by Type"
            value={selectedActionType}
            onChange={onActionTypeChange}
            defaultOptions={actionTypeOptions}
            minWidth="140px"
            isClearable={false}
          />
        </>
      }
      activeFilters={activeFilters}
      onReset={onReset}
      hasActiveFilters={hasActiveFilters}
    />
  );
}
